import { create } from 'zustand';
import brain from 'brain';

export interface ClientUser {
  uid: string;
  name: string;
  email?: string;
  photoURL?: string;
  role?: 'client' | 'limited_access';
  clientId?: string; // For linking to client records
  isActive?: boolean;
  lastUpdated: number;
}

interface ClientStore {
  // State
  clientUsers: Map<string, ClientUser>;
  isLoading: boolean;
  lastFetchTime: number;
  
  // Actions
  getClientUser: (uid: string) => ClientUser | null;
  fetchClientUser: (uid: string) => Promise<ClientUser>;
  fetchAllClients: () => Promise<void>;
  loadAllClients: () => Promise<ClientUser[]>;
  updateClientUser: (uid: string, updates: Partial<ClientUser>) => void;
  clearClients: () => void;
  
  // Utility
  isClientCacheValid: (uid?: string) => boolean;
  getClientsByRole: (role: 'client' | 'limited_access') => ClientUser[];
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const FULL_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

// Track pending requests to prevent duplicates
const pendingRequests = new Map<string, Promise<ClientUser>>();

export const useClientStore = create<ClientStore>((set, get) => ({
  // Initial state
  clientUsers: new Map(),
  isLoading: false,
  lastFetchTime: 0,
  
  // Get client user from cache
  getClientUser: (uid: string) => {
    const client = get().clientUsers.get(uid);
    if (!client) return null;
    
    // Check if cache is still valid
    const now = Date.now();
    if (now - client.lastUpdated > CACHE_DURATION) {
      // Cache is stale, trigger a fetch but return current data
      get().fetchClientUser(uid);
    }
    
    return client;
  },
  
  // Fetch individual client user
  fetchClientUser: async (uid: string): Promise<ClientUser> => {
    const currentClient = get().clientUsers.get(uid);
    const now = Date.now();
    
    // Return cached data if it's fresh
    if (currentClient && (now - currentClient.lastUpdated) < CACHE_DURATION) {
      return currentClient;
    }
    
    // Check for pending request
    const pendingRequest = pendingRequests.get(uid);
    if (pendingRequest) {
      return pendingRequest;
    }
    
    // Create new request
    const requestPromise = (async () => {
      try {
        const response = await brain.get_basic_user_details({ userId: uid });
        const userData = await response.json();
        
        const clientUser: ClientUser = {
          uid,
          name: userData.name || 'Unknown User',
          email: userData.email,
          photoURL: userData.photoURL,
          role: userData.role === 'limited_access' ? 'limited_access' : 'client',
          clientId: userData.clientId,
          isActive: userData.isActive,
          lastUpdated: now
        };
        
        // Update store
        set(state => {
          const newClientUsers = new Map(state.clientUsers);
          newClientUsers.set(uid, clientUser);
          return { clientUsers: newClientUsers };
        });
        
        return clientUser;
      } catch (error) {
        console.error('Error fetching client user:', error);
        
        // Return cached data if available, otherwise fallback
        if (currentClient) {
          return currentClient;
        }
        
        const fallbackUser: ClientUser = {
          uid,
          name: 'Unknown User',
          role: 'client',
          lastUpdated: now
        };
        
        // Cache the fallback briefly
        set(state => {
          const newClientUsers = new Map(state.clientUsers);
          newClientUsers.set(uid, fallbackUser);
          return { clientUsers: newClientUsers };
        });
        
        return fallbackUser;
      } finally {
        pendingRequests.delete(uid);
      }
    })();
    
    pendingRequests.set(uid, requestPromise);
    return requestPromise;
  },
  
  // Fetch all clients (could be extended to use a bulk API endpoint)
  fetchAllClients: async () => {
    const { lastFetchTime, isLoading } = get();
    const now = Date.now();
    
    // Skip if recently fetched or already loading
    if (isLoading || (now - lastFetchTime) < FULL_REFRESH_INTERVAL) {
      return;
    }
    
    set({ isLoading: true });
    
    try {
      // This could be replaced with a bulk endpoint if available
      // For now, we'll rely on individual fetches as needed
      set({ lastFetchTime: now });
    } catch (error) {
      console.error('Error fetching all clients:', error);
    } finally {
      set({ isLoading: false });
    }
  },
  
  // Load all clients using brain.list_clients for bulk loading
  loadAllClients: async (): Promise<ClientUser[]> => {
    const { isLoading } = get();
    
    // Skip if already loading
    if (isLoading) {
      // Return cached data if available
      return Array.from(get().clientUsers.values());
    }
    
    set({ isLoading: true });
    
    try {
      const response = await brain.list_clients();
      if (!response.ok) {
        throw new Error('Failed to load clients');
      }
      
      const data = await response.json();
      const clientsDict = data.clients || {};
      const now = Date.now();
      
      // Convert dictionary to ClientUser format and cache all clients
      const clientUsers: ClientUser[] = Object.entries(clientsDict).map(([uid, name]) => ({
        uid,
        name: name as string,
        lastUpdated: now,
        role: 'client' as const,
        isActive: true
      }));
      
      // Update store with all clients
      set(state => {
        const newClientUsers = new Map(state.clientUsers);
        clientUsers.forEach(client => {
          newClientUsers.set(client.uid, client);
        });
        return { 
          clientUsers: newClientUsers,
          lastFetchTime: now
        };
      });
      
      return clientUsers;
    } catch (error) {
      console.error('Error loading all clients:', error);
      // Return cached data if available
      return Array.from(get().clientUsers.values());
    } finally {
      set({ isLoading: false });
    }
  },
  
  // Update client user in cache
  updateClientUser: (uid: string, updates: Partial<ClientUser>) => {
    set(state => {
      const currentClient = state.clientUsers.get(uid);
      if (!currentClient) return state;
      
      const updatedClient: ClientUser = {
        ...currentClient,
        ...updates,
        lastUpdated: Date.now()
      };
      
      const newClientUsers = new Map(state.clientUsers);
      newClientUsers.set(uid, updatedClient);
      return { clientUsers: newClientUsers };
    });
  },
  
  // Clear all client data
  clearClients: () => {
    set({ 
      clientUsers: new Map(), 
      lastFetchTime: 0,
      isLoading: false 
    });
    pendingRequests.clear();
  },
  
  // Check if cache is valid
  isClientCacheValid: (uid?: string) => {
    const now = Date.now();
    
    if (uid) {
      const client = get().clientUsers.get(uid);
      return client ? (now - client.lastUpdated) < CACHE_DURATION : false;
    }
    
    // Check overall cache validity
    const { lastFetchTime } = get();
    return (now - lastFetchTime) < FULL_REFRESH_INTERVAL;
  },
  
  // Get clients by role
  getClientsByRole: (role: 'client' | 'limited_access') => {
    const { clientUsers } = get();
    return Array.from(clientUsers.values()).filter(client => client.role === role);
  }
}));
